import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Rating } from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import ReviewsIcon from '@mui/icons-material/Reviews';
import AddCommentIcon from '@mui/icons-material/AddComment';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';

import { useReview } from '../../context/ReviewContext';
import { useAuth } from '../../context/AuthContext';
import ReviewCard from './ReviewCard';
import { Review } from '../../models/Review';
import { useLocation, useNavigate } from 'react-router-dom';

interface ReviewSectionProps {
  productId: number;
}

const ReviewSection: React.FC<ReviewSectionProps> = ({ productId }) => {
  const { getReviewsByProduct, createReview, getUserReviews } = useReview();
  const { isAuthenticated, user } = useAuth();

  const navigate = useNavigate();
  const location = useLocation();

  const [reviews, setReviews] = useState<Review[]>([]);
  const [totalReviews, setTotalReviews] = useState(0);
  const [averageRating, setAverageRating] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userHasReviewed, setUserHasReviewed] = useState(false);

  // Form state
  const [reviewTitle, setReviewTitle] = useState('');
  const [reviewComment, setReviewComment] = useState('');
  const [reviewRating, setReviewRating] = useState<number | null>(null);

  // Fetch reviews
  useEffect(() => {
    const loadReviews = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const result = await getReviewsByProduct(productId, currentPage, 5);
        setReviews(result.reviews);
        setTotalReviews(result.total);
        setAverageRating(result.averageRating);
        setTotalPages(result.totalPages);
      } catch (err: any) {
        setError(err.message || 'Nie udało się załadować recenzji');
        console.error('Error fetching reviews:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadReviews();
  }, [productId, currentPage, getReviewsByProduct]);

  // Check if user already reviewed
  useEffect(() => {
    const checkUserReview = async () => {
      if (!isAuthenticated || !user) return;

      try {
        const result = await getUserReviews();
        const hasReviewed = result.reviews.some(
          (review) => review.productId === productId,
        );
        setUserHasReviewed(hasReviewed);
      } catch (err) {
        console.error('Error checking user reviews:', err);
      }
    };

    checkUserReview();
  }, [isAuthenticated, user, productId, getUserReviews]);

  const handleSubmitReview = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!reviewRating) {
      setError('Proszę wybrać ocenę');
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);

      await createReview({
        productId,
        rating: reviewRating,
        title: reviewTitle,
        comment: reviewComment,
      });

      // Refresh reviews
      const result = await getReviewsByProduct(productId, 1, 5);
      setReviews(result.reviews);
      setTotalReviews(result.total);
      setAverageRating(result.averageRating);
      setTotalPages(result.totalPages);
      setCurrentPage(1);

      // Reset form
      setReviewTitle('');
      setReviewComment('');
      setReviewRating(null);
      setShowReviewForm(false);
      setUserHasReviewed(true);
    } catch (err: any) {
      setError(err.message || 'Nie udało się dodać recenzji');
      console.error('Error submitting review:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Renderowanie oceny jako gwiazdki
  const renderRatingStars = (rating: number) => {
    return (
      <div className="flex text-amber-500">
        {[...Array(5)].map((_, i) =>
          i < Math.round(rating) ? (
            <StarIcon key={i} className="h-6 w-6" />
          ) : (
            <StarOutlineIcon key={i} className="h-6 w-6" />
          ),
        )}
      </div>
    );
  };

  // Style dla przycisków
  const buttonVariants = {
    initial: { scale: 0.95, opacity: 0 },
    animate: {
      scale: 1,
      opacity: 1,
      transition: { duration: 0.3, type: 'spring' },
    },
    hover: {
      scale: 1.05,
      boxShadow: '0 5px 15px rgba(0, 0, 0, 0.1)',
      transition: { duration: 0.2 },
    },
    tap: { scale: 0.95 },
  };

  return (
    <motion.section
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="mb-12"
    >
      <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
        <ReviewsIcon className="mr-2" /> Opinie klientów
      </h2>

      {/* Podsumowanie recenzji */}
      <div className="bg-gray-50 rounded-xl p-6 mb-8 border border-gray-100">
        <div className="flex flex-col md:flex-row items-center justify-between">
          <div className="flex flex-col items-center md:items-start mb-4 md:mb-0">
            <div className="flex items-baseline gap-2">
              <span className="text-4xl font-bold text-gray-900">
                {averageRating.toFixed(1)}
              </span>
              <span className="text-lg text-gray-500">/ 5</span>
            </div>
            <div className="mt-2">{renderRatingStars(averageRating)}</div>
            <div className="mt-2 text-gray-500 text-sm">
              Bazując na {totalReviews}{' '}
              {totalReviews === 1
                ? 'opinii'
                : totalReviews > 1 && totalReviews < 5
                  ? 'opiniach'
                  : 'opinii'}
            </div>
          </div>

          <div>
            <motion.button
              onClick={() => {
                if (!isAuthenticated) {
                  navigate('/logowanie', { state: { from: location } });
                }
                setShowReviewForm(!showReviewForm);
              }}
              disabled={userHasReviewed}
              className={`rounded-full px-5 py-3 flex items-center gap-2 font-medium ${
                userHasReviewed
                  ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                  : 'bg-primary text-white hover:bg-primary/90'
              }`}
              variants={buttonVariants}
              initial="initial"
              animate="animate"
              whileHover={userHasReviewed || !isAuthenticated ? {} : 'hover'}
              whileTap={userHasReviewed || !isAuthenticated ? {} : 'tap'}
            >
              <AddCommentIcon fontSize="small" />
              {userHasReviewed
                ? 'Już oceniłeś ten produkt'
                : !isAuthenticated
                  ? 'Zaloguj się, aby ocenić'
                  : 'Dodaj opinię'}
            </motion.button>
            {!isAuthenticated && (
              <p className="text-xs text-gray-500 mt-2">
                Musisz być zalogowany, aby dodać opinię
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Formularz dodawania opinii */}
      <AnimatePresence>
        {showReviewForm && isAuthenticated && !userHasReviewed && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden mb-8"
          >
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-lg font-semibold mb-4">Dodaj swoją opinię</h3>

              {error && (
                <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmitReview}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Twoja ocena*
                  </label>
                  <Rating
                    name="review-rating"
                    value={reviewRating}
                    onChange={(_, newValue) => setReviewRating(newValue)}
                    precision={1}
                    size="large"
                  />
                </div>

                <div className="mb-4">
                  <label
                    htmlFor="review-title"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Tytuł
                  </label>
                  <input
                    id="review-title"
                    type="text"
                    value={reviewTitle}
                    onChange={(e) => setReviewTitle(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Podsumuj swoją opinię w jednym zdaniu"
                  />
                </div>

                <div className="mb-4">
                  <label
                    htmlFor="review-comment"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Komentarz
                  </label>
                  <textarea
                    id="review-comment"
                    value={reviewComment}
                    onChange={(e) => setReviewComment(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    rows={4}
                    placeholder="Opisz swoją opinię..."
                  />
                </div>

                <div className="flex justify-end gap-4 mt-6">
                  <motion.button
                    type="button"
                    onClick={() => setShowReviewForm(false)}
                    className="inline-flex items-center justify-center rounded-full px-6 py-3 bg-gray-100 text-gray-700 font-medium"
                    variants={buttonVariants}
                    initial="initial"
                    animate="animate"
                    whileHover="hover"
                    whileTap="tap"
                  >
                    <CloseIcon fontSize="small" className="mr-1" />
                    <span>Anuluj</span>
                  </motion.button>

                  <motion.button
                    type="submit"
                    disabled={isSubmitting || !reviewRating}
                    className={`inline-flex items-center justify-center rounded-full px-6 py-3 font-medium ${
                      isSubmitting || !reviewRating
                        ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                        : 'bg-primary text-white'
                    }`}
                    variants={buttonVariants}
                    initial="initial"
                    animate="animate"
                    whileHover={isSubmitting || !reviewRating ? {} : 'hover'}
                    whileTap={isSubmitting || !reviewRating ? {} : 'tap'}
                  >
                    {isSubmitting ? (
                      <div className="flex items-center">
                        <span className="mr-2">Wysyłanie</span>
                        <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></div>
                      </div>
                    ) : (
                      <>
                        <span>Opublikuj opinię</span>
                        <SendIcon fontSize="small" className="ml-1" />
                      </>
                    )}
                  </motion.button>
                </div>
              </form>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Lista recenzji */}
      <div className="space-y-4">
        {isLoading ? (
          // Skeleton loader
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div
                key={i}
                className="bg-white rounded-lg p-5 animate-pulse border border-gray-100"
              >
                <div className="flex justify-between">
                  <div className="w-1/3 h-4 bg-gray-200 rounded"></div>
                  <div className="w-1/4 h-4 bg-gray-200 rounded"></div>
                </div>
                <div className="mt-3 w-1/5 h-3 bg-gray-200 rounded"></div>
                <div className="mt-4 space-y-2">
                  <div className="h-3 bg-gray-200 rounded"></div>
                  <div className="h-3 bg-gray-200 rounded w-5/6"></div>
                </div>
              </div>
            ))}
          </div>
        ) : reviews.length === 0 ? (
          <div className="text-center py-10 bg-gray-50 rounded-lg border border-gray-100">
            <motion.div
              className="inline-flex items-center justify-center w-20 h-20 bg-gray-100 rounded-full mb-6"
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5, type: 'spring' }}
            >
              <ReviewsIcon className="text-gray-500" style={{ fontSize: 48 }} />
            </motion.div>
            <h3 className="mt-2 text-lg font-medium text-gray-900">
              Brak opinii
            </h3>
            <p className="mt-1 text-gray-500">
              Ten produkt nie ma jeszcze żadnych opinii.{' '}
              {isAuthenticated && !userHasReviewed ? (
                <motion.button
                  onClick={() => setShowReviewForm(true)}
                  className="text-primary hover:underline font-medium"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Bądź pierwszy!
                </motion.button>
              ) : null}
            </p>
          </div>
        ) : (
          <>
            <div className="space-y-4">
              {reviews.map((review) => (
                <ReviewCard key={review.id} review={review} />
              ))}
            </div>

            {/* Paginacja */}
            {totalPages > 1 && (
              <div className="flex justify-center mt-8">
                <div className="flex items-center gap-2">
                  <motion.button
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                    className={`px-5 py-3 rounded-full flex items-center justify-center ${
                      currentPage === 1
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                    }`}
                    variants={buttonVariants}
                    initial="initial"
                    animate="animate"
                    whileHover={currentPage === 1 ? {} : 'hover'}
                    whileTap={currentPage === 1 ? {} : 'tap'}
                  >
                    &laquo; Poprzednia
                  </motion.button>

                  <div className="px-4 py-2 bg-white border border-gray-200 rounded-md text-gray-700">
                    Strona {currentPage} z {totalPages}
                  </div>

                  <motion.button
                    disabled={currentPage === totalPages}
                    onClick={() =>
                      setCurrentPage((p) => Math.min(p + 1, totalPages))
                    }
                    className={`px-5 py-3 rounded-full flex items-center justify-center ${
                      currentPage === totalPages
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                    }`}
                    variants={buttonVariants}
                    initial="initial"
                    animate="animate"
                    whileHover={currentPage === totalPages ? {} : 'hover'}
                    whileTap={currentPage === totalPages ? {} : 'tap'}
                  >
                    Następna &raquo;
                  </motion.button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </motion.section>
  );
};

export default ReviewSection;
